import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "../../lib/AuthContext";
import {
  Broadcast, Plus, CircleNotch, Trash, Eye, UploadSimple, ArrowRight,
  ArrowLeft, Lock, Crown, CheckCircle, ShieldWarning, Warning, X,
  MagnifyingGlass, CaretRight, DownloadSimple, Clock, FileText,
} from "@phosphor-icons/react";
import { Button } from "../../components/ui/button";
import ToolInfoFooter from "../../components/ToolInfoFooter";
import { MONITORING_TOOL } from "../../lib/toolCopy";

export default function DashboardMonitoring() {
  const { user, authFetch } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedList, setSelectedList] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const tier = user?.tier || "free";
  const canMonitor = tier !== "free";

  const loadLists = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${window.location.origin}/api/monitoring/lists`);
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [authFetch]);

  useEffect(() => { if (canMonitor) loadLists(); else setLoading(false); }, [canMonitor, loadLists]);

  if (!canMonitor) {
    return (
      <>
        <Helmet><title>Bulk Monitoring | NPI Lookup</title></Helmet>
        <div className="" data-testid="dashboard-monitoring">
          <h1 className="font-display font-bold text-2xl text-slate-900 mb-6">Bulk Monitoring</h1>
          <div className="bg-white rounded-xl border-2 border-slate-200 p-12 text-center">
            <Lock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h2 className="font-display font-bold text-xl text-slate-900 mb-2">Monitoring requires a Pro plan</h2>
            <p className="text-sm text-slate-500 max-w-md mx-auto mb-6">Track providers for PECOS, OIG, and NPI status changes. Get alerts when compliance statuses change.</p>
            <Link to="/pricing"><Button className="bg-teal hover:bg-teal/90 text-white rounded-xl gap-2 h-11 px-6"><Crown className="w-4 h-4" weight="fill" /> Upgrade to Pro</Button></Link>
          </div>
        </div>
      </>
    );
  }

  if (selectedList) {
    return <ListDetailView listId={selectedList} authFetch={authFetch} onBack={() => { setSelectedList(null); loadLists(); }} tier={tier} userId={user?.auth0_id} />;
  }

  const lists = data?.lists || [];
  const limits = data?.limits || {};
  const totalProviders = data?.total_providers || 0;
  const totalAlerts = lists.reduce((s, l) => s + (l.alert_count || 0), 0);

  return (
    <>
      <Helmet><title>Bulk Monitoring | NPI Lookup</title></Helmet>
      <div className="" data-testid="dashboard-monitoring">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display font-bold text-2xl text-slate-900">Bulk Monitoring</h1>
            <p className="text-sm text-slate-500">{MONITORING_TOOL.tagline}</p>
          </div>
          <Button onClick={() => setShowCreate(true)} className="bg-teal hover:bg-teal/90 text-white rounded-lg gap-2" data-testid="create-list-btn">
            <Plus className="w-4 h-4" /> New List
          </Button>
        </div>

        {/* Summary row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <MiniStat label="Lists" value={lists.length} max={limits.lists} />
          <MiniStat label="Providers Monitored" value={totalProviders} max={limits.providers} />
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className={`text-2xl font-bold ${totalAlerts > 0 ? "text-red-600" : "text-slate-900"}`}>{totalAlerts}</p>
            <p className="text-xs text-slate-500">Unacknowledged Alerts</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <p className="text-sm font-medium text-slate-600">
              {lists.length > 0 && lists[0].last_checked
                ? new Date(lists[0].last_checked).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
                : "Never"}
            </p>
            <p className="text-xs text-slate-500">Last Check</p>
          </div>
        </div>

        {/* Create modal */}
        {showCreate && <CreateListModal authFetch={authFetch} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); loadLists(); }} />}

        {/* Lists */}
        {loading ? (
          <div className="p-12 text-center"><CircleNotch className="w-8 h-8 text-teal animate-spin mx-auto" /></div>
        ) : lists.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <Broadcast className="w-12 h-12 text-slate-300 mx-auto mb-4" weight="fill" />
            <h3 className="font-display font-semibold text-lg text-slate-900 mb-2">No monitoring lists yet</h3>
            <p className="text-sm text-slate-500 mb-4">Create your first list and add providers to start tracking compliance changes.</p>
            <Button onClick={() => setShowCreate(true)} className="bg-teal hover:bg-teal/90 text-white rounded-lg gap-2">
              <Plus className="w-4 h-4" /> Create First List
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {lists.map(lst => (
              <button key={lst.list_id} onClick={() => setSelectedList(lst.list_id)}
                className="w-full bg-white rounded-xl border border-slate-200 p-5 flex items-center justify-between hover:border-slate-300 hover:shadow-sm transition-all text-left group" data-testid={`list-${lst.list_id}`}>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-display font-semibold text-slate-900 group-hover:text-teal transition-colors">{lst.name}</h3>
                    {lst.alert_count > 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-600 text-xs font-semibold rounded-full">
                        <Warning className="w-3 h-3" weight="fill" /> {lst.alert_count} alert{lst.alert_count > 1 ? "s" : ""}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500">{lst.provider_count || 0} providers{lst.description ? ` — ${lst.description}` : ""}</p>
                  {lst.last_checked && <p className="text-xs text-slate-400 mt-1">Last checked: {new Date(lst.last_checked).toLocaleString()}</p>}
                </div>
                <CaretRight className="w-5 h-5 text-slate-400 group-hover:text-teal shrink-0" />
              </button>
            ))}
          </div>
        )}

        <ToolInfoFooter title={MONITORING_TOOL.title} steps={MONITORING_TOOL.steps} faqs={MONITORING_TOOL.faqs} />
      </div>
    </>
  );
}

// ── List Detail View ────────────────────────────────────────────

function ListDetailView({ listId, authFetch, onBack, tier, userId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [addNpis, setAddNpis] = useState("");
  const [addingBulk, setAddingBulk] = useState(false);
  const [addResult, setAddResult] = useState(null);
  const [changes, setChanges] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${window.location.origin}/api/monitoring/lists/${listId}?page=${page}&per_page=25`);
      if (res.ok) setData(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [authFetch, listId, page]);

  useEffect(() => { load(); }, [load]);

  const checkAll = async () => {
    setChecking(true);
    try {
      await authFetch(`${window.location.origin}/api/monitoring/lists/${listId}/check`, { method: "POST" });
      await load();
    } catch (e) { console.error(e); }
    finally { setChecking(false); }
  };

  const addProviders = async () => {
    const raw = addNpis.trim();
    if (!raw) return;
    // Parse NPIs from pasted text — one per line, comma-separated, or space-separated
    const npis = raw.split(/[\n,\s]+/).map(s => s.trim().replace(/\D/g, "")).filter(s => s.length === 10);
    if (npis.length === 0) return;
    setAddingBulk(true); setAddResult(null);
    let added = 0, failed = 0;
    for (const npi of [...new Set(npis)]) {
      try {
        const res = await authFetch(`${window.location.origin}/api/monitoring/lists/${listId}/add`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ npi }),
        });
        if (res.ok) added++; else failed++;
      } catch { failed++; }
    }
    setAddResult({ added, failed, total: npis.length });
    setAddingBulk(false);
    load();
  };

  const removeProvider = async (npi) => {
    setRemoving(npi);
    try {
      await authFetch(`${window.location.origin}/api/monitoring/lists/${listId}/remove/${npi}`, { method: "DELETE" });
      load();
    } catch (e) { console.error(e); }
    finally { setRemoving(null); }
  };

  const viewChanges = async (npi) => {
    try {
      const res = await authFetch(`${window.location.origin}/api/monitoring/lists/${listId}/changes?npi=${npi}`);
      if (res.ok) { const d = await res.json(); setChanges({ npi, items: d.changes }); }
    } catch (e) { console.error(e); }
  };

  const acknowledgeChanges = async (npi) => {
    try {
      await authFetch(`${window.location.origin}/api/monitoring/changes/acknowledge`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ list_id: listId, npi }),
      });
      setChanges(null); load();
    } catch (e) { console.error(e); }
  };

  const exportCsv = () => {
    if (!data?.providers) return;
    const hdr = "Provider Name,NPI,Specialty,State,PECOS,OIG,Last Checked\n";
    const rows = data.providers.map(p =>
      `"${p.provider_name || ""}","${p.npi}","${p.specialty || ""}","${p.state || ""}","${p.pecos_status === true ? "Enrolled" : p.pecos_status === false ? "Not Enrolled" : "Unchecked"}","${p.oig_status === true ? "Excluded" : p.oig_status === false ? "Clear" : "Unchecked"}","${p.last_checked || ""}"`
    ).join("\n");
    const blob = new Blob([hdr + rows], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${data.list?.name || "monitoring"}.csv`; a.click();
  };

  const lst = data?.list || {};
  const providers = data?.providers || [];
  const totalPages = data?.total_pages || 1;

  return (
    <div className="">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button onClick={onBack} className="text-slate-400 hover:text-slate-600"><ArrowLeft className="w-5 h-5" /></button>
        <div className="flex-1 min-w-0">
          <h1 className="font-display font-bold text-2xl text-slate-900 truncate">{lst.name || "List"}</h1>
          {lst.description && <p className="text-sm text-slate-500">{lst.description}</p>}
          {lst.last_checked && <p className="text-xs text-slate-400">Last checked: {new Date(lst.last_checked).toLocaleString()}</p>}
        </div>
      </div>

      {/* Actions bar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Button onClick={checkAll} disabled={checking} className="bg-navy hover:bg-navy/90 text-white rounded-lg gap-2 text-sm" data-testid="check-all-btn">
          {checking ? <CircleNotch className="w-4 h-4 animate-spin" /> : <Broadcast className="w-4 h-4" />} Check All Now
        </Button>
        <Button onClick={() => setShowAdd(!showAdd)} variant="outline" className="rounded-lg gap-2 text-sm"><Plus className="w-4 h-4" /> Add Provider</Button>
        <Button onClick={() => setShowUpload(!showUpload)} variant="outline" className="rounded-lg gap-2 text-sm"><UploadSimple className="w-4 h-4" /> Upload CSV</Button>
        <Button onClick={exportCsv} variant="outline" className="rounded-lg gap-2 text-sm"><DownloadSimple className="w-4 h-4" /> Export</Button>
      </div>

      {/* Add Providers — multi-NPI paste */}
      {showAdd && (
        <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-slate-700">Add Providers by NPI</p>
            <button onClick={() => { setShowAdd(false); setAddResult(null); }} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
          </div>
          <textarea
            value={addNpis}
            onChange={e => setAddNpis(e.target.value)}
            placeholder={"Paste NPI numbers — one per line, comma-separated, or space-separated:\n1234567890\n0987654321\n1122334455"}
            rows={5}
            className="w-full text-sm font-mono border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal/30 mb-3"
          />
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-400">
              {addNpis.trim() ? `${addNpis.split(/[\n,\s]+/).filter(s => s.trim().replace(/\D/g, "").length === 10).length} valid NPIs detected` : "Supports copy-paste from spreadsheets"}
            </p>
            <div className="flex items-center gap-2">
              {addResult && (
                <span className="text-xs text-emerald-600 font-medium">
                  {addResult.added} added{addResult.failed > 0 ? `, ${addResult.failed} failed` : ""}
                </span>
              )}
              <Button onClick={addProviders} disabled={addingBulk || !addNpis.trim()} className="bg-teal hover:bg-teal/90 text-white rounded-lg text-sm gap-2">
                {addingBulk ? <CircleNotch className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* CSV Upload */}
      {showUpload && <CsvUpload listId={listId} authFetch={authFetch} onDone={() => { setShowUpload(false); load(); }} onClose={() => setShowUpload(false)} />}

      {/* Provider Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center"><CircleNotch className="w-6 h-6 text-teal animate-spin mx-auto" /></div>
        ) : providers.length === 0 ? (
          <div className="p-12 text-center">
            <Broadcast className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <h3 className="font-display font-semibold text-slate-900 mb-1">No providers in this list</h3>
            <p className="text-sm text-slate-500">Add providers manually or upload a CSV to start monitoring.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">Provider</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600">NPI</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600 hidden md:table-cell">Specialty</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600 hidden lg:table-cell">State</th>
                  <th className="text-center px-4 py-3 font-semibold text-slate-600">PECOS</th>
                  <th className="text-center px-4 py-3 font-semibold text-slate-600">OIG</th>
                  <th className="text-center px-4 py-3 font-semibold text-slate-600">Changes</th>
                  <th className="text-left px-4 py-3 font-semibold text-slate-600 hidden md:table-cell">Checked</th>
                  <th className="text-right px-4 py-3 font-semibold text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {providers.map(p => (
                  <tr key={p.npi} className="border-b border-slate-100 hover:bg-slate-50/50">
                    <td className="px-4 py-3"><Link to={`/provider/${p.npi}`} className="font-medium text-slate-900 hover:text-teal">{p.provider_name || "—"}</Link></td>
                    <td className="px-4 py-3 font-mono text-slate-600 text-xs">{p.npi}</td>
                    <td className="px-4 py-3 text-slate-600 max-w-[140px] truncate hidden md:table-cell">{p.specialty || "—"}</td>
                    <td className="px-4 py-3 text-slate-600 hidden lg:table-cell">{p.state || "—"}</td>
                    <td className="px-4 py-3 text-center"><StatusPill value={p.pecos_status} trueText="Enrolled" falseText="Not Enrolled" /></td>
                    <td className="px-4 py-3 text-center"><StatusPill value={p.oig_status} trueText="EXCLUDED" falseText="Clear" isWarning /></td>
                    <td className="px-4 py-3 text-center">
                      {p.change_count > 0 ? (
                        <button onClick={() => viewChanges(p.npi)}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-amber-50 text-amber-700 text-xs font-semibold rounded-lg hover:bg-amber-100 transition-colors"
                          data-testid={`review-changes-${p.npi}`}>
                          <Warning className="w-3 h-3" weight="fill" /> {p.change_count}
                          <span className="underline underline-offset-2 ml-0.5">Review</span>
                        </button>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-600 text-xs font-semibold rounded-full">
                          <CheckCircle className="w-3 h-3" weight="fill" /> OK
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap hidden md:table-cell">
                      {p.last_checked ? new Date(p.last_checked).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "Never"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link to={`/provider/${p.npi}`} className="text-slate-400 hover:text-teal"><Eye className="w-4 h-4" /></Link>
                        <button onClick={() => removeProvider(p.npi)} disabled={removing === p.npi} className="text-slate-400 hover:text-red-500">
                          {removing === p.npi ? <CircleNotch className="w-4 h-4 animate-spin" /> : <Trash className="w-4 h-4" />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm text-slate-500">
          <span>Page {page} of {totalPages}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="rounded-lg"><ArrowLeft className="w-4 h-4" /></Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="rounded-lg"><ArrowRight className="w-4 h-4" /></Button>
          </div>
        </div>
      )}

      {/* Review Changes Modal */}
      {changes && (() => {
        const provider = providers.find(p => p.npi === changes.npi) || {};
        const critical = changes.items.some(c => ["oig_status", "pecos_status", "npi_status"].includes(c.field_changed));
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setChanges(null)}>
            <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h2 className="font-display font-bold text-lg text-slate-900">Review Changes</h2>
                    {critical && <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-700 text-[10px] font-bold rounded-full"><ShieldWarning className="w-3 h-3" weight="fill" /> CRITICAL</span>}
                  </div>
                  <p className="text-sm text-slate-500">
                    <span className="font-medium text-slate-700">{provider.provider_name || changes.items[0]?.provider_name || "Provider"}</span>
                    <span className="mx-1">·</span>
                    <span className="font-mono text-xs">NPI {changes.npi}</span>
                  </p>
                </div>
                <button onClick={() => setChanges(null)} className="text-slate-400 hover:text-slate-600 p-1"><X className="w-5 h-5" /></button>
              </div>

              <div className="overflow-y-auto px-6 py-4 flex-1">
                {changes.items.length === 0 ? (
                  <div className="text-center py-10">
                    <CheckCircle className="w-10 h-10 text-emerald-500 mx-auto mb-2" weight="fill" />
                    <p className="text-sm text-slate-600">No changes recorded yet.</p>
                  </div>
                ) : (
                  <>
                    <p className="text-xs text-slate-500 mb-3 uppercase tracking-wider font-semibold">
                      {changes.items.length} change{changes.items.length > 1 ? "s" : ""} detected
                    </p>
                    <div className="space-y-3">
                      {changes.items.map((c, i) => {
                        const fieldLabel = c.field_label || c.field_changed?.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
                        const isCritical = ["oig_status", "pecos_status", "npi_status"].includes(c.field_changed);
                        return (
                          <div key={i} className={`border rounded-xl overflow-hidden ${c.acknowledged ? "border-slate-200 opacity-75" : "border-slate-200"}`}>
                            <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 flex items-center justify-between gap-2">
                              <div className="flex items-center gap-2">
                                <span className="font-semibold text-sm text-slate-900">{fieldLabel}</span>
                                {isCritical && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-red-100 text-red-700 text-[9px] font-bold rounded-full uppercase">Critical</span>}
                                {c.acknowledged && <span className="text-[10px] text-slate-400 font-medium">Acknowledged</span>}
                              </div>
                              <span className="text-[10px] text-slate-400">{new Date(c.detected_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-slate-200">
                              <div className="px-4 py-3 bg-red-50/30">
                                <p className="text-[10px] font-semibold text-red-500 uppercase tracking-wider mb-1">Previous</p>
                                <p className="text-sm text-slate-700 break-words line-through decoration-red-300/70">{c.old_value || "—"}</p>
                              </div>
                              <div className="px-4 py-3 bg-emerald-50/30">
                                <p className="text-[10px] font-semibold text-emerald-600 uppercase tracking-wider mb-1">Current</p>
                                <p className="text-sm text-slate-900 font-medium break-words">{c.new_value || "—"}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>

              <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-between bg-slate-50">
                <Link to={`/provider/${changes.npi}`} className="text-sm text-teal font-medium hover:underline flex items-center gap-1">
                  <Eye className="w-4 h-4" /> Open full provider profile
                </Link>
                <div className="flex items-center gap-2">
                  <Button onClick={() => setChanges(null)} variant="outline" size="sm" className="rounded-lg">Close</Button>
                  {changes.items.some(c => !c.acknowledged) && (
                    <Button onClick={() => acknowledgeChanges(changes.npi)} size="sm" className="bg-slate-800 hover:bg-slate-900 text-white rounded-lg">
                      Acknowledge All
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ── Sub Components ──────────────────────────────────────────────

function CreateListModal({ authFetch, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleCreate = async () => {
    if (!name.trim()) { setError("Name required"); return; }
    setSaving(true); setError("");
    try {
      const res = await authFetch(`${window.location.origin}/api/monitoring/lists`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), description: desc.trim() }),
      });
      if (res.ok) { onCreated(); } else {
        const d = await res.json(); setError(d.detail || "Failed to create");
      }
    } catch { setError("Failed to create"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <h2 className="font-display font-bold text-lg text-slate-900 mb-4">Create Monitoring List</h2>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">List Name *</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Westside Medical Group"
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal/30" autoFocus />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
            <input type="text" value={desc} onChange={e => setDesc(e.target.value)} placeholder="Optional description..."
              className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal/30" />
          </div>
          {error && <p className="text-sm text-red-500">{error}</p>}
        </div>
        <div className="flex items-center justify-end gap-2 mt-5">
          <Button onClick={onClose} variant="outline" className="rounded-lg">Cancel</Button>
          <Button onClick={handleCreate} disabled={saving} className="bg-teal hover:bg-teal/90 text-white rounded-lg gap-2">
            {saving ? <CircleNotch className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Create
          </Button>
        </div>
      </div>
    </div>
  );
}

function CsvUpload({ listId, authFetch, onDone, onClose }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await authFetch(`${window.location.origin}/api/monitoring/lists/${listId}/upload-csv`, {
        method: "POST", body: formData,
      });
      if (res.ok) { const d = await res.json(); setResult(d); }
    } catch (e) { console.error(e); }
    finally { setUploading(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
      {!result ? (
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <input type="file" accept=".csv" onChange={e => setFile(e.target.files?.[0] || null)}
              className="text-sm text-slate-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200" />
          </div>
          <Button onClick={handleUpload} disabled={!file || uploading} className="bg-teal hover:bg-teal/90 text-white rounded-lg text-sm gap-2">
            {uploading ? <CircleNotch className="w-4 h-4 animate-spin" /> : <UploadSimple className="w-4 h-4" />} Upload
          </Button>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-emerald-500" weight="fill" />
            <p className="text-sm text-slate-700">
              <span className="font-semibold text-emerald-600">{result.added}</span> providers added
              {result.already_in > 0 && <span className="text-slate-500"> ({result.already_in} already in list)</span>}
            </p>
          </div>
          <Button onClick={onDone} size="sm" variant="outline" className="rounded-lg">Done</Button>
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, max }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <p className="text-2xl font-bold text-slate-900">{value}{max && max !== -1 ? <span className="text-sm font-normal text-slate-400"> / {max}</span> : ""}</p>
      <p className="text-xs text-slate-500">{label}</p>
    </div>
  );
}

function StatusPill({ value, trueText, falseText, isWarning }) {
  if (value === null || value === undefined) return <span className="text-[11px] text-slate-400">Unchecked</span>;
  if (isWarning) {
    return value
      ? <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-700 text-[11px] font-semibold rounded-full"><ShieldWarning className="w-3 h-3" weight="fill" />{trueText}</span>
      : <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[11px] font-semibold rounded-full"><CheckCircle className="w-3 h-3" weight="fill" />{falseText}</span>;
  }
  return value
    ? <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[11px] font-semibold rounded-full"><CheckCircle className="w-3 h-3" weight="fill" />{trueText}</span>
    : <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-500 text-[11px] font-semibold rounded-full">{falseText}</span>;
}
